import React, { useEffect, useMemo, useRef } from 'react'
import gql from 'graphql-tag'
import { useMutation, useQuery } from '@apollo/client'
import TableColumnPreferencePopover from './TableColumnPreferencePopover'
import { buildColumnConfigFromPreference, columnConfigSignature, isColumnVisible } from './tableColumnPreferences'

export { buildColumnConfigFromPreference }

const CURRENT_USER_QUERY = gql`
    query CurrentUserQuery {
        currentUser {
            id
            member {
                id
            }
        }
    }
`

const MEMBER_TABLE_PREFERENCE_QUERY = gql`
    query MemberTablePreferenceQuery($memberId: ID!, $tableId: String!) {
        memberTablePreference(memberId: $memberId, tableId: $tableId)
    }
`

const UPDATE_MEMBER_TABLE_PREFERENCE = gql`
    mutation updateMemberTablePreference($input: UpdateMemberTablePreferenceInput!) {
        updateMemberTablePreference(input: $input) {
            ok
            errors {
                messages
                field
            }
        }
    }
`

// The Customize Columns picker with the view saved on the member, so it follows them across browsers.
// For a view kept in this browser only, see LocalStorageColumnPreferenceControl.
function TableColumnPreferenceControl({ tableId, defaultColumnConfig = [], columnConfig = [], setColumnConfig, buttonLabel = 'PREFERENCE', placement = 'left', buttonId }) {
    const lastAppliedConfigSignatureRef = useRef(null)
    const lastTableIdRef = useRef(tableId)
    const applyColumnConfig = typeof setColumnConfig === 'function' ? setColumnConfig : () => {}

    const { data: currentUserData, loading: currentUserLoading } = useQuery(CURRENT_USER_QUERY, {
        fetchPolicy: 'cache-first',
    })
    const memberId = currentUserData?.currentUser?.member?.id
    const normalizedDefaultColumns = useMemo(
        () => buildColumnConfigFromPreference(defaultColumnConfig, []),
        [defaultColumnConfig]
    )

    const { data, error: preferenceError, loading, refetch } = useQuery(MEMBER_TABLE_PREFERENCE_QUERY, {
        variables: { memberId, tableId },
        skip: !memberId || !tableId,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    })

    const [updateMemberTablePreference, { loading: saving }] = useMutation(UPDATE_MEMBER_TABLE_PREFERENCE)

    // Applied exactly once per table, as soon as the saved preference is known -- or
    // as soon as it is known there is none. Callers may treat the first call to
    // setColumnConfig as "the preference has landed" (TaskManagementViewContainer
    // holds My Tasks back until then, because the config feeds its query variables),
    // so every settled outcome below has to resolve rather than stall.
    useEffect(() => {
        // A stale signature from the previous table would otherwise suppress this
        // table's first publish outright if it happened to match (e.g. two tables
        // that both resolve to "everything visible"). Consumers that gate their own
        // render on the first call to setColumnConfig (My Tasks does) would then
        // never see it and spin forever.
        if (lastTableIdRef.current !== tableId) {
            lastTableIdRef.current = tableId
            lastAppliedConfigSignatureRef.current = null
        }

        let nextConfig = null

        if (!memberId) {
            // Still fetching the member: falling back to defaults now would publish a
            // config the saved preference is about to replace.
            if (currentUserLoading) return
            nextConfig = normalizedDefaultColumns
        } else if (!tableId) {
            // Nothing to load a preference against; the query above is skipped.
            nextConfig = normalizedDefaultColumns
        } else if (!loading && (data || preferenceError)) {
            // On error there is no stored list to read, and buildColumnConfigFromPreference
            // treats a missing one as "everything visible" -- the same config the caller
            // seeded itself with, so this only ever settles, never surprises.
            nextConfig = buildColumnConfigFromPreference(defaultColumnConfig, data?.memberTablePreference)
        }

        if (!nextConfig) return

        const nextConfigSignature = columnConfigSignature(nextConfig)

        if (nextConfigSignature !== lastAppliedConfigSignatureRef.current) {
            lastAppliedConfigSignatureRef.current = nextConfigSignature
            applyColumnConfig(nextConfig)
        }
    }, [memberId, currentUserLoading, tableId, loading, data, preferenceError, defaultColumnConfig, normalizedDefaultColumns, applyColumnConfig])

    const savePreference = async (draftColumnConfig) => {
        if (!memberId || !tableId) return false

        let saveError = null
        const response = await updateMemberTablePreference({
            variables: {
                input: {
                    memberId,
                    tableId,
                    preferences: draftColumnConfig.filter((column) => isColumnVisible(column)).map((column) => column.id),
                },
            },
        }).catch((mutationError) => {
            saveError = mutationError.message
            return null
        })
        if (saveError) return saveError

        const result = response?.data?.updateMemberTablePreference
        if (!result) return false

        if (result.ok) {
            applyColumnConfig(draftColumnConfig)
            await refetch()
            return true
        }

        const firstError = result?.errors?.[0]
        return firstError?.messages?.[0] || 'An unknown error occurred.'
    }

    return (
        <TableColumnPreferencePopover
            buttonId={buttonId}
            buttonLabel={buttonLabel}
            placement={placement}
            columnConfig={columnConfig}
            defaultColumns={normalizedDefaultColumns}
            onSave={savePreference}
            busy={loading || saving}
            saving={saving}
        />
    )
}

export default TableColumnPreferenceControl
